import React from 'react';
import { Box, Tooltip } from '@mui/material';

/**
 * Fit-to-viewport table policy (VAT / Bookings pattern):
 * - table is always width 100% with table-layout:fixed
 * - columns use % widths (colgroup); one “flex” column may omit width and take remainder
 * - cells ellipsis-truncate; never set a table minWidth that exceeds the viewport
 * - TableContainer uses overflowX: hidden (vertical scroll OK)
 */

export const FIT_TABLE_CONTAINER_SX = {
  width: '100%',
  maxWidth: '100%',
  overflowX: 'hidden' as const,
};

export const FIT_TABLE_SX = {
  width: '100%',
  maxWidth: '100%',
  tableLayout: 'fixed' as const,
  '& .MuiTableCell-root': {
    px: 0.6,
    py: 0.45,
    fontSize: '0.8125rem',
    verticalAlign: 'middle',
    overflow: 'hidden',
  },
  /** Force % columns to clip instead of expanding the table. */
  '& .MuiTableCell-root:not(.fit-table-no-clip)': {
    maxWidth: 0,
  },
  '& .MuiTableCell-root.fit-table-no-clip': {
    maxWidth: 'none',
    overflow: 'visible',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    textAlign: 'center',
    whiteSpace: 'normal',
    lineHeight: 1.15,
    verticalAlign: 'bottom',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-root': {
    justifyContent: 'center',
    alignItems: 'flex-end',
    width: '100%',
    color: 'inherit',
    height: 'auto',
    whiteSpace: 'normal',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-root.Mui-active': {
    color: 'inherit',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-icon': {
    position: 'static',
    marginLeft: '2px',
    marginBottom: '2px',
    opacity: 0.7,
  },
  /**
   * Hover fill belongs on the cells of the hovered row. Background on `tr`
   * paints on the neighbouring row in this clipped sticky table.
   */
  '& .MuiTableBody-root .MuiTableRow-hover:hover:not(.Mui-selected)': {
    backgroundColor: 'transparent',
  },
  '& .MuiTableBody-root .MuiTableRow-hover:hover:not(.Mui-selected) > .MuiTableCell-root': {
    backgroundColor: 'action.hover',
  },
  '& .MuiTableBody-root .MuiChip-root': {
    overflow: 'hidden',
    verticalAlign: 'middle',
  },
};

export const fitTruncateSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  maxWidth: '100%',
};

export const fitChipSx = {
  maxWidth: '100%',
  height: 22,
  maxHeight: 22,
  overflow: 'hidden',
  '& .MuiChip-label': {
    px: 0.6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    lineHeight: '22px',
    py: 0,
  },
};

/** Table tooltips sit beside the cell so they do not cover the row above or below. */
export const fitTableTooltipProps = {
  placement: 'right' as const,
  disableInteractive: true,
  slotProps: {
    popper: {
      sx: { pointerEvents: 'none' },
    },
  },
};

export const FitCellText: React.FC<{
  title?: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ title, children, testId }) => (
  <Tooltip title={title || ''} disableHoverListener={!title} {...fitTableTooltipProps}>
    <Box component="span" sx={fitTruncateSx} data-testid={testId}>
      {children}
    </Box>
  </Tooltip>
);

/** Multi-line header label (``\\n`` → line break). */
export function fitHeaderLabel(label: string): React.ReactElement {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        textAlign: 'center',
        whiteSpace: 'pre-line',
        lineHeight: 1.15,
        fontWeight: 700,
      }}
    >
      {label}
    </Box>
  );
}

export type FitColDef = {
  key: string;
  /** Percentage of table width, e.g. 8.5. Omit for the flex/remainder column. */
  pct?: number;
};

/** Build colgroup entries from percentage defs (omit pct for remainder column). */
export function buildFitColgroup(cols: FitColDef[]): Array<{ key: string; width?: string }> {
  return cols.map((c) =>
    c.pct != null && Number.isFinite(c.pct)
      ? { key: c.key, width: `${c.pct}%` }
      : { key: c.key }
  );
}

/**
 * Equal-width columns (optional flexKey omits width so that column absorbs remainder).
 * Useful for quick conversion of simple list tables.
 */
export function equalFitCols(
  keys: string[],
  opts?: { flexKey?: string; /** total % allocated to non-flex cols; default 100 if no flex */ nonFlexTotal?: number }
): FitColDef[] {
  const flexKey = opts?.flexKey;
  const nonFlex = keys.filter((k) => k !== flexKey);
  if (!flexKey || nonFlex.length === keys.length) {
    const pct = Number((100 / Math.max(1, keys.length)).toFixed(3));
    return keys.map((key) => ({ key, pct }));
  }
  const pool = opts?.nonFlexTotal ?? 78;
  const each = Number((pool / Math.max(1, nonFlex.length)).toFixed(3));
  return keys.map((key) => (key === flexKey ? { key } : { key, pct: each }));
}

/** Renders a ``<colgroup>`` from FitColDef / buildFitColgroup output. */
export function FitColgroup({
  cols,
}: {
  cols: Array<FitColDef | { key: string; width?: string }>;
}): React.ReactElement {
  const normalized = cols.map((c): { key: string; width?: string } => {
    if ('width' in c && c.width != null) return { key: c.key, width: c.width };
    const pct = (c as FitColDef).pct;
    return pct != null ? { key: c.key, width: `${pct}%` } : { key: c.key };
  });
  return (
    <colgroup>
      {normalized.map((col) => (
        <col key={col.key} style={col.width ? { width: col.width } : undefined} />
      ))}
    </colgroup>
  );
}

/**
 * Classification Review compact-column policy (also used on Annual Returns / VAT TX tables):
 * pin ID / date / amounts / chips to content-fitted px; Description/Supplier/Category share leftover.
 */
export const FIT_FLEX_CELL_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  maxWidth: 0,
  width: 'auto',
  boxSizing: 'border-box' as const,
  px: '4px',
} as const;

/** Pin a compact column so leftover table width cannot inflate it. */
export function compactColSx(px: number) {
  return {
    width: `${px}px !important`,
    minWidth: `${px}px !important`,
    maxWidth: `${px}px !important`,
    boxSizing: 'border-box' as const,
    overflow: 'hidden' as const,
  };
}

/** Like {@link compactColSx} but never ellipsis-truncates cell content. */
export function fitContentColSx(px: number) {
  return {
    width: `${px}px !important`,
    minWidth: `${px}px !important`,
    maxWidth: `${px}px !important`,
    boxSizing: 'border-box' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'visible' as const,
  };
}

/** Approximate rendered width (px) for 0.75–0.81rem tabular-ish UI text. */
export function estimateFitTextPx(text: string): number {
  let w = 0;
  for (const ch of String(text || '')) {
    if (ch >= '0' && ch <= '9') w += 7.4;
    else if (ch === '.' || ch === ',' || ch === ' ') w += 3.6;
    else if (ch === '€' || ch === '£' || ch === '¥') w += 8.5;
    else if (ch === '-' || ch === '+') w += 5.5;
    else w += 7.2;
  }
  return w;
}

export function clampFitColPx(px: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.ceil(px)));
}
